import { inject } from "@angular/core";
import { hasUserRoleManager, isAdmin, isProjectManager } from "@app/_helpers/permission";
import { uniqRulebreakTypeFn } from "@app/_helpers/rulebreaks/utils";
import { evaluateWorkingHours, WorkingHourViolation } from "@app/_helpers/rulebreakUtil";
import { useProjectFind, useUserFind } from "@app/_helpers/useUtils";
import { useUserFindInProject } from "@app/_helpers/utils";
import { computeGapBreakEntries } from "@app/services/mixed-times-working-hours.calc";
import RoundingConfigData from "@app/shared/dialogs/rounding-dialog/models/rounding-config-data";
import { RoundingTypes } from "@app/shared/dialogs/rounding-dialog/rounding-dialog.component";
import {
	nearestFutureMinutes,
	nearestMinutes,
	nearestPastMinutes,
} from "@app/shared/dialogs/rounding-dialog/rounding-functions";
import { ComegoTime, Time, UserSettingsQuery } from "@timeghost/web-api";
import { addDays, isSameDay, roundToNearestMinutes, startOfDay } from "date-fns/esm";
import pick from "lodash-es/pick";
import { map } from "rxjs/operators";

export type ReportType = "workinghours" | "projecthours";
export type ReportItem<T extends ComegoTime | Time | {} = {}> = {
	id: string;
	name: string;
	start: Date;
	end: Date | null;
	recording: boolean;
	duration: number | null;
	entity: string;
	userId: string;
	workspaceId: string;
	timeZone?: string;
	currentUserCanEdit: boolean;
} & (T extends ComegoTime
	? { violations: WorkingHourViolation[]; type: { id: string | null; name: "work" | "pause" | "absence" } }
	: {}) &
	(T extends Time
		? {
				clientId: string;
				clientName: string;
				projectId: string;
				projectName: string;
				projectIsPM: boolean;
				projectIsMember: boolean;
				projectArchived: boolean;
				projectIsDefault?: boolean;
				taskId?: string;
				taskName?: string;
				billable: boolean;
				amount?: number;
				cost?: number;
				tags?: { id: string; name: string }[];
				inputMode: "range" | "duration";
			}
		: {});

type WorkingHourRules = {
	dailyMaxHours?: number;
	breakBetweenWorkingDays?: number;
	minimumBreakDurationForCredit?: number;
	breaks?: Array<{ workingHours: number; minutesDue: number }>;
};

/** Normalize ReportItem (type.name) for client `evaluateWorkingHours` (type string). */
function toEvalComego(item: ReportItem<ComegoTime>): ComegoTime {
	return {
		...item,
		type: item.type?.name ?? "work",
		user: { id: item.userId },
	} as unknown as ComegoTime;
}

/** Last / first `work` on calendar-adjacent days from the same fetch slice. */
function pickAdjacentWorkFromItems(
	sortedTimes: ComegoTime[],
	centerDate: Date
): { prevDayWork: ComegoTime | null; nextDayWork: ComegoTime | null } {
	const refNow = new Date();
	const prevCalendarDay = addDays(centerDate, -1);
	const nextCalendarDay = addDays(centerDate, 1);
	const workOnPrev = sortedTimes.filter((t) => t.type === "work" && isSameDay(new Date(t.start), prevCalendarDay));
	const prevDayWork =
		workOnPrev.length > 0
			? workOnPrev.slice().sort((a, b) => {
					const aend = new Date(a.end || refNow).getTime();
					const bend = new Date(b.end || refNow).getTime();
					return bend - aend;
				})[0]
			: null;

	const workOnNext = sortedTimes.filter((t) => t.type === "work" && isSameDay(new Date(t.start), nextCalendarDay));
	const nextDayWork =
		workOnNext.length > 0
			? workOnNext.slice().sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime())[0]
			: null;

	return { prevDayWork, nextDayWork };
}

/**
 * Project hours have no pause rows; gaps between same-day work entries are treated as pauses
 * so break-credit evaluation matches real working hours.
 */
function withGapPausesForEvaluation(dayEval: ComegoTime[]): ComegoTime[] {
	const refNow = new Date();
	const workEntries = dayEval
		.filter((t) => t.type === "work")
		.map((t) => {
			const start = new Date(t.start);
			const end = new Date(t.end || refNow);
			return {
				start,
				end,
				durationMs: Math.max(0, end.getTime() - start.getTime()),
				type: "work" as const,
			};
		});
	const gapPauses = computeGapBreakEntries(workEntries).map(
		(gap) =>
			({
				id: null,
				name: null,
				type: "pause",
				start: gap.start,
				end: gap.end,
				user: dayEval[0] && "user" in dayEval[0] ? dayEval[0].user : undefined,
			}) as unknown as ComegoTime
	);
	if (!gapPauses.length) return dayEval;
	return [...dayEval, ...gapPauses].sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
}

/**
 * Runs break / rest / maxHours checks for legacy-mapped project hours displayed as working hours.
 * Uses the full merged list for day adjacency; only attaches violations onto `legacyProjectHourIds`.
 */
export function attachLegacyProjectHourViolations(
	allItems: ReportItem<ComegoTime>[],
	legacyProjectHourIds: Set<string>,
	workingHourRules: WorkingHourRules | null | undefined
): ReportItem<ComegoTime>[] {
	if (!workingHourRules || !legacyProjectHourIds.size || !allItems.length) return allItems;

	const rules = {
		...workingHourRules,
		breaks: workingHourRules.breaks ?? [],
	};
	const violationsById = new Map<string, WorkingHourViolation[]>();

	const byUser = new Map<string, ReportItem<ComegoTime>[]>();
	for (const item of allItems) {
		const list = byUser.get(item.userId) ?? [];
		list.push(item);
		byUser.set(item.userId, list);
	}

	for (const userItems of byUser.values()) {
		const sorted = userItems.slice().sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
		const evalSorted = sorted.map(toEvalComego);

		const daysWithLegacy = new Set<number>();
		for (const item of sorted) {
			if (legacyProjectHourIds.has(item.id)) {
				daysWithLegacy.add(startOfDay(new Date(item.start)).getTime());
			}
		}

		for (const dayTs of daysWithLegacy) {
			const day = new Date(dayTs);
			const dayEval = withGapPausesForEvaluation(
				sorted.filter((i) => isSameDay(new Date(i.start), day)).map(toEvalComego)
			);
			const { prevDayWork, nextDayWork } = pickAdjacentWorkFromItems(evalSorted, day);
			const violations = evaluateWorkingHours(dayEval, rules, day, {
				prevDayItem: prevDayWork,
				nextDayItem: nextDayWork,
			});

			const seenDayErrors = new Set<string>();
			for (const violation of violations) {
				if (!violation.entryID || !legacyProjectHourIds.has(violation.entryID)) continue;
				const key = uniqRulebreakTypeFn(violation);
				if (seenDayErrors.has(key)) continue;
				seenDayErrors.add(key);
				const existing = violationsById.get(violation.entryID) ?? [];
				existing.push(violation);
				violationsById.set(violation.entryID, existing);
			}
		}
	}

	if (!violationsById.size) return allItems;

	return allItems.map((item) => {
		if (!legacyProjectHourIds.has(item.id)) return item;
		const violations = violationsById.get(item.id);
		if (!violations?.length) return item;
		return { ...item, violations };
	});
}

export function useTransformTimeToReportItem<T extends ComegoTime | Time, R extends ReportItem<T>[]>(): (
	...entities: T[]
) => R {
	const userFindInProject = useUserFindInProject();
	const userSettings = inject(UserSettingsQuery).getValue();
	return (...entities: T[]): R => {
		if (entities.length === 0) return [] as R;
		const cosmosEntityName = entities[0]["cosmosEntityName"] as "comego" | "times";
		const entityType = {
			comego: "workinghours",
			times: "projecthours",
		}[cosmosEntityName];
		const newItems = entities.map((entity) => {
			const start = new Date(entity.start);
			const end = entity.end ? new Date(entity.end) : null;
			const recording = !entity.end;
			const duration = end ? (end.getTime() - start.getTime()) / 1000 : null;
			const projectPerm = "project" in entity ? userFindInProject(entity.user.id, entity.project.id) : false;
			const editable = projectPerm || !!(entity as Time).currentUserCanEdit || isAdmin(userSettings);
			return {
				id: entity.id,
				start,
				end,
				recording,
				duration,
				entity: entityType,
				userId: entity.user.id,
				name: entity.name,
				...(entity.timeZone && { timeZone: entity.timeZone }),
				...(entityType === "workinghours" &&
					"type" in entity && {
						type: { id: entity["typeId"] as string, name: entity.type },
					}),
				...(entityType === "projecthours" &&
					"project" in entity && {
						editable,
						currentUserCanEdit: editable,
						amount: entity.amount ?? 0,
						cost: entity.cost ?? 0,
						duration: entity.timeDiff,
						inputMode: entity["inputMode"] as string,
						billable: entity.billable,
						clientId: entity.client.id,
						clientName: entity.client.name,
						projectId: entity.project.id,
						projectName: entity.project.name,
						projectIsPM: projectPerm && projectPerm.role === "manager",
						projectIsMember: !!projectPerm,
						projectArchived: entity.project.completed,
						projectIsDefault: entity.project.useAsDefault ? true : undefined,
						tags: entity.tags?.map((tag) => ({ id: tag.id, name: tag.name })) ?? [],
						...(entity.task && { taskId: entity.task.id, taskName: entity.task.name }),
					}),
			};
		});
		return newItems as unknown as R;
	};
}
export function useTransformReportItemToTime<R extends Time, T extends ReportItem<R>>(): (entity: T) => R {
	const userFind = useUserFind();
	const projectFinder = useProjectFind();
	const userSettingsQuery = inject(UserSettingsQuery);
	const userSettings = userSettingsQuery.getValue();
	return (entity: T) => {
		const user = userFind(entity.userId);
		const project = projectFinder(entity.projectId);
		const editable =
			isAdmin(userSettings) ||
			hasUserRoleManager(userSettings) ||
			entity.userId == userSettings.id ||
			isProjectManager(userSettings.id, userSettings.workspace, project);
		const now = roundToNearestMinutes(new Date(), { nearestTo: 1 });
		return {
			id: entity.id,
			name: entity.name,
			cosmosEntityName: "times",
			start: entity.start.toISOString(),
			end: entity.end?.toISOString(),
			timeDiff: entity.recording ? (now.getTime() - entity.start.getTime()) / 1000 : entity.duration,
			workspace: { id: entity.workspaceId },
			timeZone: entity.timeZone,
			currentUserCanEdit: editable,
			editable,
			inputMode: entity.inputMode,
			user: user ? pick(user, "id", "name") : { id: entity.userId },
			client: {
				id: entity.clientId,
				name: entity.clientName,
			},
			project: entity.projectId
				? {
						id: entity.projectId,
						name: entity.projectName,
						completed: project?.completed ?? entity.projectArchived,
					}
				: undefined,
			task: entity.taskId
				? {
						id: entity.taskId,
						name: entity.taskName,
					}
				: undefined,
			billable: entity.billable,
			tags: entity.tags?.map((tag) => ({ id: tag.id, name: tag.name })) ?? [],
			amount: entity.amount,
			cost: entity.cost,
			useAsDefault: !!entity.projectIsDefault,
		} as unknown as R;
	};
}

export function reportsRoundingHandler<T extends ReportItem>(overrideEnabled?: boolean) {
	return map(([data, rounding, ...args]: [T[], RoundingConfigData, ...any[]]) => {
		data =
			(!data && []) ||
			data.map((_y) => {
				const y: T & { viewEnd?: string; viewStart?: string } = <T>{
					..._y,
				};
				if (overrideEnabled === undefined ? rounding?.enabled === true : overrideEnabled) {
					if (rounding.type === RoundingTypes.Nearest) {
						y.duration = nearestMinutes(rounding.minutes, y.duration * 1000) * 60;
					} else if (rounding.type === RoundingTypes.UpTo) {
						y.duration = nearestFutureMinutes(rounding.minutes, y.duration * 1000) * 60;
					} else if (rounding.type === RoundingTypes.DownTo) {
						y.duration = nearestPastMinutes(rounding.minutes, y.duration * 1000) * 60;
					}
					const st = new Date(y.start.getTime() + y.duration * 1000);
					if (new Date(y.start) <= st) y.viewEnd = st.toISOString();
					if (y.duration < 0) y.duration = 0;
				}
				return y;
			});
		return [data, ...args] as [T[], ...any[]];
	});
}
